import { useState, useEffect, useCallback } from 'react';
import { api, extractErrorMessage } from '../api/client';

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApi<T>(endpoint: string | null, deps: unknown[] = []): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(endpoint !== null);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!endpoint) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<T>(endpoint);
      setData(result);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, ...deps]);

  useEffect(() => {
    if (endpoint) fetch();
    else { setData(null); setLoading(false); }
  }, [fetch, endpoint]);

  return { data, loading, error, refetch: fetch };
}

export function usePaginatedApi<T>(
  endpoint: string,
  page: number,
  pageSize: number = 20,
  deps: unknown[] = [],
) {
  const url = `${endpoint}?page=${page}&limit=${pageSize}`;
  return useApi<T>(url, [page, pageSize, ...deps]);
}
